import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, Typography, Box, Paper, Grid, TextField, 
  Button, FormControlLabel, Checkbox, Slider, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Card, CardContent, List, ListItem, ListItemText,
  CircularProgress, Alert, Snackbar, IconButton, Rating,

  LinearProgress,
  Stack, Chip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';

import axios from 'axios';
import moment from 'moment';
import { submitQAEvaluation, getCallForAnalysis } from '../services/qaService';
import { getCallRecording } from '../services/aiService';

// QA Criteria definition (same as CallAnalysis for consistency)
const QA_CRITERIA = {
  communication: { name: 'Communication Skills', maxScore: 20 },
  knowledge: { name: 'Product/Service Knowledge', maxScore: 20 },
  process: { name: 'Process Adherence', maxScore: 25 },
  customerService: { name: 'Customer Service', maxScore: 20 },
  compliance: { name: 'Compliance & Legal', maxScore: 15 }
};

const CallReview = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [call, setCall] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [scores, setScores] = useState([]);
  const [overallScore, setOverallScore] = useState(0);
  const [notes, setNotes] = useState('');

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  
  // Load call details and metrics
  useEffect(() => {
    const fetchCallData = async () => {
      setLoading(true);
      try {
        console.log('Fetching call data for ID:', callId);
        // Get call details using the correct service
        const callResponse = await getCallForAnalysis(callId);
        console.log('Call response:', callResponse);
        const callData = callResponse.data.call;
        console.log('Call data structure:', callData);
        console.log('CallQAScores:', callData.CallQAScores);
        console.log('All evaluations:', callData.all_evaluations);
        console.log('QA evaluation:', callData.qa_evaluation);
        setCall(callData);
        
        // Use all_evaluations instead of CallQAScores (backend sends it as all_evaluations)
        const qaScores = callData.all_evaluations || [];
        
        // Pre-fill notes if they exist - prioritize most recent evaluation
        let notesValue = '';
        if (qaScores && qaScores.length > 0) {
          // Sort by reviewDate or created_at to get the most recent evaluation first
          const sortedScores = qaScores.sort((a, b) => {
            const dateA = new Date(a.reviewDate || a.created_at || 0);
            const dateB = new Date(b.reviewDate || b.created_at || 0);
            return dateB - dateA; // Most recent first
          });
          
          console.log('=== NOTES EXTRACTION DEBUG ===');
          console.log('All QA Scores:', qaScores);
          console.log('Sorted QA Scores (most recent first):', sortedScores);
          
          // Get the most recent QA score
          const latestQAScore = sortedScores[0];
          console.log('Latest QA Score:', latestQAScore);
          console.log('Latest QA Score notes field:', latestQAScore.notes);
          
          notesValue = latestQAScore.notes || '';
          console.log('Extracted notes value:', notesValue);
        } else if (callData.notes) {
          // Fallback to call notes
          notesValue = callData.notes;
          console.log('Using fallback call notes:', notesValue);
        }
        
        console.log('Final notes value being set:', notesValue);
        setNotes(notesValue);
        
        // Pre-fill overall score if it exists - extract from JSONB structure using same sorted approach
        let overallScoreValue = 0;
        if (qaScores && qaScores.length > 0) {
          // Use the same sorted scores to ensure consistency
          const sortedScores = qaScores.sort((a, b) => {
            const dateA = new Date(a.reviewDate || a.created_at || 0);
            const dateB = new Date(b.reviewDate || b.created_at || 0);
            return dateB - dateA; // Most recent first
          });
          
          const qaScore = sortedScores[0]; // Use the most recent QA score
          // Try multiple sources for overall score
          overallScoreValue = qaScore.score || 
                             qaScore.metrics?.total_score || 
                             qaScore.metrics?.overall_score ||
                             qaScore.metrics?.score || 0;
          
          console.log('Found overall score from QA evaluation:', overallScoreValue);
          
          // If no direct overall score, calculate from categories
          if (!overallScoreValue && qaScore.metrics?.categories) {
            const categories = qaScore.metrics.categories;
            let totalScore = 0;
            let totalMaxScore = 0;
            
            Object.keys(QA_CRITERIA).forEach(categoryKey => {
              if (categories[categoryKey] !== undefined) {
                const categoryScore = categories[categoryKey];
                const maxScore = QA_CRITERIA[categoryKey].maxScore;
                totalScore += categoryScore;
                totalMaxScore += maxScore;
              }
            });
            
            if (totalMaxScore > 0) {
              overallScoreValue = Math.round((totalScore / totalMaxScore) * 100);
              console.log('Calculated overall score from categories:', totalScore, '/', totalMaxScore, '=', overallScoreValue, '%');
            }
          }
        } else if (callData.qaScore) {
          // Fallback to legacy qa_score field
          overallScoreValue = typeof callData.qaScore === 'number' ? callData.qaScore : 0;
        }
        setOverallScore(overallScoreValue);
        
        // Use the same QA criteria as CallAnalysis for consistency
        const QA_CRITERIA = {
          communication: { name: 'Communication Skills', maxScore: 20 },
          knowledge: { name: 'Product/Service Knowledge', maxScore: 20 },
          process: { name: 'Process Adherence', maxScore: 25 },
          customerService: { name: 'Customer Service', maxScore: 20 },
          compliance: { name: 'Compliance & Legal', maxScore: 15 }
        };
        
        const metricsArray = Object.keys(QA_CRITERIA).map(key => ({
          id: key,
          name: QA_CRITERIA[key].name,
          maxScore: QA_CRITERIA[key].maxScore,
          weight: 1
        }));
        setMetrics(metricsArray);
        
        // Initialize scores array - extract from JSONB metrics structure using same logic as CallAnalysis
        const initialScores = metricsArray.map(metric => {
          let existingScore = 0;
          let existingComment = '';
          
          console.log('Processing metric:', metric.name, 'ID:', metric.id);
          
          // Extract score from QA evaluations JSONB metrics field - use most recent evaluation
          if (qaScores && qaScores.length > 0) {
            // Use the same sorted approach for consistency
            const sortedScores = qaScores.sort((a, b) => {
              const dateA = new Date(a.reviewDate || a.created_at || 0);
              const dateB = new Date(b.reviewDate || b.created_at || 0);
              return dateB - dateA; // Most recent first
            });
            
            const qaScore = sortedScores[0]; // Get the most recent QA score
            const metricsData = qaScore.metrics || {};
            console.log('QA Score metrics data (most recent):', metricsData);
            
            const categoryKey = metric.id; // Use the metric ID directly since it matches the category key
            console.log('Using category key:', categoryKey);
            
            // Try categories first (manual evaluations) - these are stored as raw scores out of maxScore
            if (metricsData.categories && metricsData.categories[categoryKey] !== undefined) {
              const rawScore = metricsData.categories[categoryKey];
              // Convert raw score to 0-100 scale for the rating component
              const maxScore = metric.maxScore || 20;
              existingScore = Math.round((rawScore / maxScore) * 100);
              console.log('Found category score:', rawScore, '/', maxScore, '-> percentage:', existingScore);
            }
            // Try detailed_scores (AI evaluations) - these might be percentages or raw scores
            else if (metricsData.detailed_scores && metricsData.detailed_scores[categoryKey]) {
              const scoreData = metricsData.detailed_scores[categoryKey];
              console.log('Found detailed score data:', scoreData);
              if (typeof scoreData === 'object') {
                // If it's an object, try percentage first, then score
                existingScore = scoreData.percentage || scoreData.score || 0;
                existingComment = scoreData.feedback || scoreData.comment || '';
              } else if (typeof scoreData === 'number') {
                // If it's a direct number, assume it's already a percentage
                existingScore = scoreData;
              }
              console.log('Extracted score:', existingScore, 'comment:', existingComment);
            }
            
            // Try to extract comments from detailed_scores first (from Comparative Analysis)
            if (!existingComment && metricsData.detailed_scores && metricsData.detailed_scores[categoryKey]) {
              const scoreData = metricsData.detailed_scores[categoryKey];
              if (typeof scoreData === 'object') {
                existingComment = scoreData.feedback || scoreData.comment || '';
                if (existingComment) {
                  console.log('Found metric comment from detailed_scores:', existingComment);
                }
              }
            }
            
            // Try to extract comments from metric_comments field (from Call Review submissions)
            if (!existingComment && metricsData.metric_comments) {
              // Look for metric-specific comments in the metric_comments field
              const metricCommentsText = metricsData.metric_comments;
              if (typeof metricCommentsText === 'string' && metricCommentsText.includes(metric.name)) {
                // Try to extract comment for this specific metric
                const metricPattern = new RegExp(`${metric.name}[:\\s]*([^\\n\\r]+)`, 'i');
                const match = metricCommentsText.match(metricPattern);
                if (match && match[1]) {
                  existingComment = match[1].trim();
                  console.log('Found metric comment from metric_comments:', existingComment);
                }
              }
            }
            
            // Fallback: Try to extract comments from notes field (legacy)
            if (!existingComment && qaScore.notes) {
              // Look for metric-specific comments in the notes
              const notesText = qaScore.notes;
              if (typeof notesText === 'string' && notesText.includes(metric.name)) {
                // Try to extract comment for this specific metric
                const metricPattern = new RegExp(`${metric.name}[:\\s]*([^\\n\\r]+)`, 'i');
                const match = notesText.match(metricPattern);
                if (match && match[1]) {
                  existingComment = match[1].trim();
                  console.log('Found metric comment from notes (legacy):', existingComment);
                }
              }
            }
          }
          
          const result = {
            metricId: metric.id,
            score: existingScore,
            comment: existingComment
          };
          console.log('Final score result for', metric.name, ':', result);
          return result;
        });
        
        setScores(initialScores);
        
        // Always try to load audio for the call (same approach as Calls page)
        loadAudio(callData.id);
        
        // Recalculate overall score after loading scores - use the scores we just calculated
        setTimeout(() => {
          if (initialScores.length > 0) {
            let totalWeight = 0;
            let weightedSum = 0;
            
            initialScores.forEach(score => {
              const metric = metricsArray.find(m => m.id === score.metricId);
              if (metric && score.score > 0) {
                const weight = metric.weight || 1;
                totalWeight += weight;
                weightedSum += score.score * weight;
              }
            });
            
            const calculatedScore = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : overallScoreValue;
            console.log('Calculated overall score from initial scores:', calculatedScore);
            setOverallScore(calculatedScore);
          } else {
            calculateOverallScore();
          }
        }, 100);
      } catch (error) {
        console.error('Error loading call data:', error);
        console.error('Error details:', error.response?.data || error.message);
        setNotification({
          open: true,
          message: `Failed to load call data: ${error.response?.data?.error || error.message}`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchCallData();
  }, [callId]);
  
  // Cleanup audio URL when component unmounts
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);
  
  // Load audio with proper authentication (same approach as Calls page)
  const loadAudio = async (callId) => {
    setAudioLoading(true);
    try {
      console.log('Loading audio for call:', callId);
      console.log('About to call getCallRecording...');
      // Use the same getCallRecording function that works in Calls page
      const audioBlob = await getCallRecording(callId);
      console.log('Audio blob received:', audioBlob);
      console.log('Audio blob size:', audioBlob.size);
      console.log('Audio blob type:', audioBlob.type);
      
      if (audioBlob.size === 0) {
        throw new Error('Audio file is empty');
      }
      
      const audioObjectUrl = URL.createObjectURL(audioBlob);
      setAudioUrl(audioObjectUrl);
      console.log('Audio blob loaded successfully, URL:', audioObjectUrl);
      
      // Try to set duration from call data if available
      if (call && call.duration && call.duration > 0) {
        console.log('Setting duration from call data:', call.duration);
        setDuration(call.duration);
      }
    } catch (error) {
      console.error('Error loading audio:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      // Don't show notification for missing audio files - just log it
      if (error.response?.status === 404) {
        console.log('Audio file not found for call', callId);
      } else {
        setNotification({
          open: true,
          message: 'Failed to load audio: ' + (error.response?.data?.message || error.message),
          severity: 'error'
        });
      }
    } finally {
      setAudioLoading(false);
    }
  };
  
  // Recalculate overall score when scores change
  useEffect(() => {
    if (scores.length > 0 && metrics.length > 0) {
      calculateOverallScore();
    }
  }, [scores, metrics]);
  
  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    
    if (audio) {
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };
      
      const handleDurationChange = () => {
        console.log('Audio duration changed:', audio.duration);
        console.log('Audio readyState:', audio.readyState);
        console.log('Audio networkState:', audio.networkState);
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          setDuration(audio.duration);
        } else {
          console.log('Invalid duration detected, keeping current duration');
        }
      };
      
      const handleEnded = () => {
        setIsPlaying(false);
      };
      
      // Add event listeners for audio events
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('durationchange', handleDurationChange);
      audio.addEventListener('loadedmetadata', handleDurationChange); // Also listen for metadata load
      audio.addEventListener('loadeddata', handleDurationChange); // Listen for data load
      audio.addEventListener('canplay', handleDurationChange); // Listen for when can start playing
      audio.addEventListener('canplaythrough', handleDurationChange); // Listen for when can play through
      audio.addEventListener('ended', handleEnded);
      
      // Try to load metadata immediately if audio is ready
      if (audio.readyState >= 1) { // HAVE_METADATA
        console.log('Audio already has metadata, setting duration immediately');
        handleDurationChange();
      }
      
      // Cleanup
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('durationchange', handleDurationChange);
        audio.removeEventListener('loadedmetadata', handleDurationChange);
        audio.removeEventListener('loadeddata', handleDurationChange);
        audio.removeEventListener('canplay', handleDurationChange);
        audio.removeEventListener('canplaythrough', handleDurationChange);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [audioRef.current]);
  
  const togglePlayPause = async () => {
    if (audioRef.current) {
      try {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          console.log('Attempting to play audio from:', audioRef.current.src);
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('Audio play error:', error);
        setNotification({
          open: true,
          message: 'Unable to play audio: ' + error.message,
          severity: 'error'
        });
      }
    } else {
      console.log('Audio ref not available');
    }
  };
  
  const handleSeek = (event, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };
  
  // Format time in MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  // Update score for a metric
  const handleScoreChange = (metricId, value) => {
    console.log('Score changed for metric:', metricId, 'new value:', value);
    setScores(prevScores => 
      prevScores.map(item => 
        item.metricId === metricId 
          ? { ...item, score: value } 
          : item
      )
    );
    // Overall score will be recalculated by useEffect
  };
  
  // Update comment for a metric
  const handleCommentChange = (metricId, comment) => {
    setScores(prevScores => 
      prevScores.map(item => 
        item.metricId === metricId 
          ? { ...item, comment } 
          : item
      )
    );
  };
  
  // Calculate overall score based on weighted metrics
  const calculateOverallScore = () => {
    console.log('Calculating overall score with scores:', scores, 'metrics:', metrics);
    
    // If there are no metrics with scores, return 0
    if (scores.length === 0) {
      console.log('No scores available, setting overall score to 0');
      setOverallScore(0);
      return;
    }
    
    let totalWeight = 0;
    let weightedSum = 0;
    
    scores.forEach(score => {
      const metric = metrics.find(m => m.id === score.metricId);
      console.log('Processing score:', score, 'found metric:', metric);
      if (metric && score.score > 0) {
        const weight = metric.weight || 1;
        totalWeight += weight;
        weightedSum += score.score * weight;
        console.log('Added score:', score.score, 'weight:', weight, 'running total:', weightedSum);
      }
    });
    
    // Calculate weighted average
    const calculatedScore = totalWeight > 0 
      ? Math.round(weightedSum / totalWeight) 
      : 0;
    
    console.log('Calculated overall score:', calculatedScore);
    setOverallScore(calculatedScore);
  };
  
  // Submit review
  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      // Convert scores array to the format expected by submitQAEvaluation
      const evaluationScores = {};
      const evaluationComments = [];
      
      scores.forEach(score => {
        const metric = metrics.find(m => m.id === score.metricId);
        if (metric) {
          // Map metric names to category keys for CallAnalysis compatibility
          const categoryKeyMap = {
            'Communication Skills': 'communication',
            'Product/Service Knowledge': 'knowledge', 
            'Process Adherence': 'process',
            'Customer Service': 'customerService',
            'Compliance & Legal': 'compliance'
          };
          
          const categoryKey = categoryKeyMap[metric.name];
          if (categoryKey && score.score > 0) {
            // Convert percentage back to raw score based on max score
            const maxScore = metric.maxScore || 20;
            const rawScore = Math.round((score.score / 100) * maxScore);
            evaluationScores[categoryKey] = rawScore;
            
            if (score.comment) {
              evaluationComments.push(`${metric.name}: ${score.comment}`);
            }
          }
        }
      });
      
      // Prepare evaluation data in the format expected by submitQAEvaluation
      const evaluationData = {
        scores: evaluationScores,
        comments: notes, // Always use the overall notes from the Notes field
        recommendations: '',
        overallRating: overallScore > 80 ? 'excellent' : overallScore > 60 ? 'good' : 'average',
        // Include individual metric comments in the metrics structure
        metricComments: evaluationComments.length > 0 ? evaluationComments.join('\n\n') : ''
      };
      
      // Submit using the correct service
      await submitQAEvaluation(callId, evaluationData);
      
      setNotification({
        open: true,
        message: 'QA review submitted successfully',
        severity: 'success'
      });
      
      // Navigate back to list after short delay
      setTimeout(() => {
        navigate('/qa-review');
      }, 2000);
    } catch (error) {
      console.error('Error submitting review:', error);
      setNotification({
        open: true,
        message: 'Failed to submit review',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle notification close
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };
  

  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (!call) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Call not found</Alert>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/qa-review')}
          sx={{ mt: 2 }}
        >
          Back to QA Review
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/qa-review')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          QA Review - Call #{call.unique_id || call.uniqueId || call.id}
        </Typography>
      </Box>
      
      {/* Call details card */}
      <Paper sx={{ mb: 4, p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Call Details</Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Date/Time" 
                  secondary={(() => {
                    console.log('Call date fields:', {
                      created_at: call.created_at,
                      createdAt: call.createdAt,
                      call_date: call.call_date,
                      id: call.id
                    });
                    
                    if (call.created_at) return moment(call.created_at).format('YYYY-MM-DD HH:mm:ss');
                    if (call.createdAt) return moment(call.createdAt).format('YYYY-MM-DD HH:mm:ss');
                    if (call.call_date) return moment(call.call_date).format('YYYY-MM-DD HH:mm:ss');
                    
                    // Fallback: try to extract from unique_id if it contains timestamp
                    if (call.unique_id && call.unique_id.includes('_')) {
                      const parts = call.unique_id.split('_');
                      const timestampPart = parts[parts.length - 1];
                      if (timestampPart && timestampPart.length >= 10) {
                        try {
                          const timestamp = parseInt(timestampPart);
                          if (timestamp > 1000000000) { // Valid timestamp
                            return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
                          }
                        } catch (e) {
                          console.log('Could not parse timestamp from unique_id');
                        }
                      }
                    }
                    
                    return 'N/A';
                  })()} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Phone Number" 
                  secondary={call.phoneNumber || call.phone || call.lead_data?.phone_number || call.unique_id || call.id} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Duration" 
                  secondary={`${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}`} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Disposition" 
                  secondary={call.disposition} 
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Agent Information</Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Agent Name" 
                  secondary={call.agent?.name || 'Unknown'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Agent ID" 
                  secondary={call.agent?.vici_id || 'Unknown'} 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="User Group" 
                  secondary={call.agent?.user_group || call.agent?.userGroup || 'Unknown'} 
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Audio player */}
      <Paper sx={{ mb: 4, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Call Recording
        </Typography>
        
        {/* Transcription Status */}
        {call.transcription_status && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Transcription Status: 
              <span style={{ 
                color: call.transcription_status === 'completed' ? 'green' : 
                       call.transcription_status === 'processing' ? 'orange' : 
                       call.transcription_status === 'failed' ? 'red' : 'gray',
                fontWeight: 'bold',
                marginLeft: '8px'
              }}>
                {call.transcription_status}
              </span>
            </Typography>
            
            {/* Show transcription if available */}
            {call.transcription && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Transcription:
                </Typography>
                <Typography variant="body1">
                  {call.transcription}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        
        {/* Analysis Status */}
        {call.analysis_status && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Analysis Status: 
              <span style={{ 
                color: call.analysis_status === 'completed' ? 'green' : 
                       call.analysis_status === 'processing' ? 'orange' : 
                       call.analysis_status === 'failed' ? 'red' : 'gray',
                fontWeight: 'bold',
                marginLeft: '8px'
              }}>
                {call.analysis_status}
              </span>
            </Typography>
            
            {/* Show AI analysis if available */}
            {call.ai_analysis && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  AI Analysis:
                </Typography>
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {typeof call.ai_analysis === 'string' ? call.ai_analysis : JSON.stringify(call.ai_analysis, null, 2)}
                </pre>
              </Box>
            )}
          </Box>
        )}
        
        {/* Audio Player Section */}
        {(audioUrl || audioLoading) ? (
          <>
            {audioLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Loading audio...</Typography>
              </Box>
            )}
            
            {audioUrl && (
              <audio ref={audioRef} src={audioUrl} preload="metadata" />
            )}
            
            {!audioLoading && !audioUrl && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Failed to load audio recording
              </Alert>
            )}
            
            {audioUrl && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={togglePlayPause} color="primary" disabled={!audioUrl}>
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
              
              <Box sx={{ width: '100%', ml: 2, mr: 2 }}>
                <Slider
                  value={currentTime}
                  onChange={handleSeek}
                  max={duration || 100}
                  aria-label="Call position"
                />
              </Box>
              
                <Typography variant="body2" color="text.secondary">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          !audioLoading && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No audio recording available for this call
            </Alert>
          )
        )}
      </Paper>
      
      {/* QA Results Section (if existing evaluation) */}
      {(call.qa_score || call.qa_evaluation) && (
        <Paper sx={{ mb: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            QA Results
          </Typography>
          
          {/* Score Summary */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5">
                Overall Score: {(() => {
                  try {
                    const evaluationScore = call.qa_evaluation?.metrics?.total_score;
                    const simpleScore = call.qa_score;
                    
                    if (evaluationScore) return `${evaluationScore}%`;
                    if (typeof simpleScore === 'string') return simpleScore;
                    if (typeof simpleScore === 'number') return `${simpleScore}%`;
                    if (simpleScore?.total_score) return `${simpleScore.total_score}%`;
                    return 'N/A';
                  } catch (error) {
                    console.error('Error displaying score:', error);
                    return 'N/A';
                  }
                })()}
              </Typography>
              <Chip
                label={(() => {
                  try {
                    const evaluationRating = call.qa_evaluation?.metrics?.overall_rating;
                    const simpleRating = typeof call.qa_score === 'string' ? call.qa_score : call.qa_score?.overall_rating;
                    return evaluationRating || simpleRating || 'N/A';
                  } catch (error) {
                    console.error('Error displaying rating:', error);
                    return 'N/A';
                  }
                })()}
                sx={(() => {
                  try {
                    const score = call.qa_evaluation?.metrics?.total_score ||
                               (typeof call.qa_score === 'number' ? call.qa_score : call.qa_score?.total_score) || 0;
                    const rating = call.qa_evaluation?.metrics?.overall_rating || call.qa_score;
                    
                    // Simple color logic for CallReview
                    if (typeof rating === 'string') {
                      switch (rating.toLowerCase()) {
                        case 'good':
                          return { backgroundColor: '#2196F3', color: 'white' };
                        case 'average':
                          return { backgroundColor: '#FFD700', color: 'black' };
                        case 'poor':
                          return { backgroundColor: '#9E9E9E', color: 'white' };
                        case 'rejected':
                          return { backgroundColor: '#F44336', color: 'white' };
                        default:
                          return { backgroundColor: '#f5f5f5', color: '#666' };
                      }
                    }
                    
                    // Numerical score colors
                    if (score >= 80) return { backgroundColor: '#2196F3', color: 'white' };
                    if (score >= 60) return { backgroundColor: '#FFD700', color: 'black' };
                    return { backgroundColor: '#F44336', color: 'white' };
                  } catch (error) {
                    console.error('Error getting score color:', error);
                    return { backgroundColor: '#f5f5f5', color: '#666' };
                  }
                })()}
              />
            </Stack>
          </Box>

          {/* Category Scores */}
          {(() => {
            try {
              const detailedScores = call.qa_evaluation?.metrics?.detailed_scores || 
                                   call.qa_evaluation?.metrics?.categories ||
                                   (call.qa_score && typeof call.qa_score === 'object' && call.qa_score.detailed_scores);
              
              console.log('CallReview QA Results - detailed scores:', detailedScores);
              console.log('CallReview QA Results - qa_evaluation:', call.qa_evaluation);
              
              if (detailedScores) {
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Category Scores
                    </Typography>
                    {Object.keys(QA_CRITERIA).map((category) => {
                      const scoreData = detailedScores[category];
                      if (!scoreData) return null;
                      
                      return (
                        <Box key={category} sx={{ mb: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="body1">
                              {QA_CRITERIA[category]?.name || category}
                            </Typography>
                            <Typography variant="body2">
                              {(() => {
                                // Handle different score data formats
                                if (scoreData?.score && scoreData?.maxScore) {
                                  // Object with score and maxScore
                                  return `${scoreData.score}/${scoreData.maxScore} (${Math.round(scoreData.percentage || 0)}%)`;
                                } else if (typeof scoreData === 'number') {
                                  // Direct numeric score - convert back to raw score for display
                                  const maxScore = QA_CRITERIA[category]?.maxScore || 20;
                                  const rawScore = Math.round((scoreData / 100) * maxScore);
                                  return `${rawScore}/${maxScore} (${scoreData}%)`;
                                } else if (scoreData?.category || scoreData?.type) {
                                  // Categorical score
                                  return scoreData.category || scoreData.type;
                                } else {
                                  return 'N/A';
                                }
                              })()}
                            </Typography>
                          </Stack>
                          {scoreData?.percentage !== undefined ? (
                            <LinearProgress
                              variant="determinate"
                              value={scoreData.percentage}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          ) : typeof scoreData === 'number' ? (
                            <LinearProgress
                              variant="determinate"
                              value={scoreData}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          ) : (
                            <Chip
                              label={scoreData?.category || scoreData?.type || 'N/A'}
                              size="small"
                              sx={{
                                backgroundColor: 
                                  scoreData?.category === 'Good' || scoreData?.type === 'Good' ? '#4caf50' :
                                  scoreData?.category === 'Average' || scoreData?.type === 'Average' ? '#ff9800' :
                                  scoreData?.category === 'Rejected' || scoreData?.type === 'Rejected' ? '#f44336' : '#9e9e9e',
                                color: 'white'
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              }
              
              return null;
            } catch (error) {
              console.error('Error rendering QA Results:', error);
              return (
                <Typography variant="body2" color="error">
                  Error displaying QA results
                </Typography>
              );
            }
          })()}
        </Paper>
      )}
      
      {/* QA Scoring */}
      <Paper sx={{ mb: 4, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quality Assessment
        </Typography>
        
        <TableContainer sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="center">Score (0-100)</TableCell>
                <TableCell>Comments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.map((metric) => {
                const scoreItem = scores.find(s => s.metricId === metric.id);
                
                return (
                  <TableRow key={metric.id}>
                    <TableCell>
                      <Typography variant="body1">{metric.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {metric.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Rating
                        name={`rating-${metric.id}`}
                        value={scoreItem?.score / 20 || 0}
                        precision={0.5}
                        onChange={(e, newValue) => handleScoreChange(metric.id, newValue * 20)}
                        max={5}
                      />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {scoreItem?.score || 0}/100
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        multiline
                        rows={2}
                        fullWidth
                        placeholder="Enter comments for this metric..."
                        value={scoreItem?.comment || ''}
                        onChange={(e) => handleCommentChange(metric.id, e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Overall Score and Notes */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Overall Score: {overallScore}/100
            </Typography>
            <Box sx={{ width: '100%', mb: 2 }}>
              <Slider
                value={overallScore}
                onChange={(e, val) => setOverallScore(val)}
                valueLabelDisplay="auto"
                min={0}
                max={100}
                marks={[
                  { value: 0, label: '0' },
                  { value: 20, label: '20' },
                  { value: 40, label: '40' },
                  { value: 60, label: '60' },
                  { value: 80, label: '80' },
                  { value: 100, label: '100' }
                ]}
              />
            </Box>
            

          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder="Enter overall notes about this call..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              variant="outlined"
            />
          </Grid>
        </Grid>
        
        {/* Submit Button */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            endIcon={<SendIcon />}
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </Box>
      </Paper>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
      

    </Container>
  );
};

export default CallReview; 